import React, { useEffect, useRef, useState } from "react";
import ContentContainer from "../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import { PAGE_IDS } from "../../../app/data/common/pageIds";
import ProgramLogs from "./program-logs/program-logs";
import ImageHistoryLogs from "./image-history-logs/image-history-logs";
import FailedImageLogs from "./failed-imge-logs/failed-image-logs";
import RelatedEventsLogs from "./realted-events-logs/related-events-logs";
import DownloadButton from "../../../ui-components/download-button/downloadButton";
import { useDispatch, useSelector } from "react-redux";
import { failedImageLogsSelector, getFailedImageHistoryTableInfo, updateFailedImgHistoryCsvDownload } from "../../../slices/documents/failedImageLogsSlice";
import { getImageHistoryTableInfo, imageHistoryLogsSelector, updateImageHistoryCsvDownload } from "../../../slices/documents/ImageHistoryLogsSlice";
import { getProgramLogsTableInfo, programLogsSelector, updatePgmHistoryCsvDownload } from "../../../slices/documents/ProgramLogsSlice";
import { getRelatedEventsTableInfo, relatedEventsLogsSelector, updateRelatedEventsHistoryCsvDownload } from "../../../slices/documents/relatedEventsLogsSlice";
import SidePopup from "./common/table-info";
import "./imaging-logs.scss";
import Button, { ButtonThemes, ButtonSizes } from "../../../ui-components/button/button";
import { ReactComponent as InfoIcon } from "../../../images/info.svg";
import { FormikProps } from "formik";
import { RelatedEventsLogsFilterModel } from "../../../app/data/documents/relatedEventsLogsModels";

const ImageLogs: React.FC<{}> = (props) => {
    const dispatch = useDispatch();
    const failedImageLogsState = useSelector(failedImageLogsSelector);
    const imageHistoryLogsState = useSelector(imageHistoryLogsSelector);
    const programLogsState = useSelector(programLogsSelector);
    const relatedEventsLogsState = useSelector(relatedEventsLogsSelector);
    const tabs = ["Image Logs", "Program Logs", "Failed Image Logs", "Related Events"];
    const [selectedTab, setSelectedTab] = useState("Failed Image Logs");
    const [showPopup, setShowPopup] = useState(false);
    const [popupData, setPopupData] = useState<{ title: string; description: string; sections: any[][] }>({ title: "", description: "", sections: [] });
    const relatedTablefilterFormRef = useRef<FormikProps<RelatedEventsLogsFilterModel>>(null);

    const [showRelatedEvents, setShowRelatedEvents] = useState(false);
    const [eventId, setEventId] = useState<string | null>(null);

    const handleShowRelatedEvents = (eventIdValue: string) => {
        setEventId(eventIdValue);
        setShowRelatedEvents(true);
        setSelectedTab("Related Events");
    };

    useEffect(() => {
        if (showRelatedEvents) {
            relatedTablefilterFormRef.current?.setFieldValue("eventId", eventId);
            relatedTablefilterFormRef.current?.submitForm();
            setShowRelatedEvents(false);
        }
        // eslint-disable-next-line
    }, [showRelatedEvents]);

    const fetchTableInfo = () => {
        setShowPopup(true);
        if (selectedTab === "Program Logs" && !programLogsState.tableInfo.title) {
            dispatch(getProgramLogsTableInfo());
        } else if (selectedTab === "Image Logs" && !imageHistoryLogsState.tableInfo.title) {
            dispatch(getImageHistoryTableInfo());
        } else if (selectedTab === "Failed Image Logs" && !failedImageLogsState.tableInfo.title) {
            dispatch(getFailedImageHistoryTableInfo());
        } else if (selectedTab === "Related Events" && !relatedEventsLogsState.tableInfo.title) {
            dispatch(getRelatedEventsTableInfo());
        }
    };

    useEffect(() => {
        if (selectedTab === "Program Logs" && programLogsState.tableInfo.title) {
            setPopupData(programLogsState.tableInfo);
        } else if (selectedTab === "Image Logs" && imageHistoryLogsState.tableInfo.title) {
            setPopupData(imageHistoryLogsState.tableInfo);
        } else if (selectedTab === "Failed Image Logs" && failedImageLogsState.tableInfo.title) {
            setPopupData(failedImageLogsState.tableInfo);
        } else if (selectedTab === "Related Events" && relatedEventsLogsState.tableInfo.title) {
            setPopupData(relatedEventsLogsState.tableInfo);
        }
    }, [selectedTab, failedImageLogsState.tableInfo, programLogsState.tableInfo, imageHistoryLogsState.tableInfo, relatedEventsLogsState.tableInfo]);

    const generateCSV = (recordItems: any[], isImage: boolean, fileName: string, records?: any[]) => {
        let csvContent = '';
        let childHeaders: any[]

        // Define headers
        const parentHeaders = Object.keys(recordItems[0]);
        csvContent += `${parentHeaders.join(',')}`;
        if (records && records.length > 0) {
            childHeaders = Object.keys(records[0]).filter(key => isImage ? key !== 'docId' : key !== 'fileId');
            csvContent += `,${childHeaders.join(',')}`;
        }

        csvContent += `\n`;

        recordItems.forEach(parent => {
            // Convert parent row to CSV format
            const parentRow = parentHeaders.map(key => formatForCSV(parent[key])).join(',');
            csvContent += `${parentRow}\n`;
            // Get child rows matching parent docId
            if (records && records.length > 0) {
                const childRows = records
                    .filter(child => isImage ? child.docId === parent.docId : child.fileId === parent.fileId)
                    .map(child => {
                        const childData = childHeaders.map(key => formatForCSV(child[key])).join(',');
                        return `${','.repeat(parentHeaders.length)}${childData}`; // Dynamic indentation
                    });
                if (childRows.length) {
                    csvContent += childRows.join('\n') + '\n';
                }
            }
        });

        // Create and download CSV file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `${fileName}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const formatForCSV = (value: any): string => {
        const stringValue = value?.toString() ?? '';
        // Add a tab prefix for suspected datetime strings
        if (typeof value === 'string' && stringValue.match(/^\d{2}\/\d{2}\/\d{2} \d{2}:\d{2}:\d{2} [AP]M$/)) {
            return `"\t${stringValue}"`;
        }
        // General fallback with quotes
        return `"${stringValue}"`;
    };

    useEffect(() => {
        if (imageHistoryLogsState.downloadCsv) {
            const { parentRecords, childEvents } = separateEvents(imageHistoryLogsState.recordItems);
            generateCSV(parentRecords, true, 'image-history-table', childEvents);
            dispatch(updateImageHistoryCsvDownload(false));
        }
        if (relatedEventsLogsState.downloadCsv) {
            const { parentRecords, childEvents } = separateEvents(relatedEventsLogsState.recordItems);
            generateCSV(parentRecords, false, 'related-events-table', childEvents);
            dispatch(updateRelatedEventsHistoryCsvDownload(false));
        }
        if (failedImageLogsState.downloadCsv) {
            generateCSV(failedImageLogsState.records, false, 'failed-images-table');
            dispatch(updateFailedImgHistoryCsvDownload(false));
        }
        if (programLogsState.downloadCsv) {
            generateCSV(programLogsState.records, false, 'programs-logs-table');
            dispatch(updatePgmHistoryCsvDownload(false));
        }
        // eslint-disable-next-line
    }, [imageHistoryLogsState.downloadCsv, relatedEventsLogsState.downloadCsv, failedImageLogsState.downloadCsv, programLogsState.downloadCsv]);

    const handleCsvDownload = () => {
        if (selectedTab === "Image Logs") {
            dispatch(updateImageHistoryCsvDownload(true));
        } else if (selectedTab === "Program Logs") {
            dispatch(updatePgmHistoryCsvDownload(true));
        } else if (selectedTab === "Failed Image Logs") {
            dispatch(updateFailedImgHistoryCsvDownload(true));
        } else if (selectedTab === "Related Events") {
            dispatch(updateRelatedEventsHistoryCsvDownload(true));
        }
    };

    const separateEvents = (records: any[]) => {
        const parentRecords: any[] = [];
        const childEvents: any[] = [];

        records.forEach(record => {
            // Extract and push events
            if (record.events && Array.isArray(record.events)) {
                record.events.forEach((event: any) => {
                    childEvents.push(event);
                });
            }

            // Clone record and remove 'events'
            const { events, ...parentWithoutEvents } = record;
            parentRecords.push(parentWithoutEvents);
        });

        return { parentRecords, childEvents };
    };

    return (
        <ContentContainer
            className="xgs-list xgs-list--full-width"
            mods={{ "full-width": true }}
            titleComponent={
                <ContentContainerToolbar
                    pageId={PAGE_IDS.IMAGELOGS}
                    title="Image Logs"
                    mods={{ "full-width": true }}
                >
                    <DownloadButton
                        title="Download CSV"
                        spinner={failedImageLogsState.downloadCsv || imageHistoryLogsState.downloadCsv || programLogsState.downloadCsv || relatedEventsLogsState.downloadCsv}
                        disabled={
                            (selectedTab === "Image Logs" && (!imageHistoryLogsState.recordItems?.length)) ||
                            (selectedTab === "Program Logs" && (!programLogsState.records?.length)) ||
                            (selectedTab === "Failed Image Logs" && (!failedImageLogsState.records?.length)) ||
                            (selectedTab === "Related Events" && (!relatedEventsLogsState.recordItems?.length))
                        }
                        onClick={() => {
                            handleCsvDownload();
                        }}
                    />

                    <Button
                        {...props}
                        theme={ButtonThemes.transparent}
                        size={ButtonSizes.auto}
                        onClick={() => fetchTableInfo()}
                    >
                        <div className="xgs-btn__icon-container">
                            <InfoIcon />
                        </div>
                        Table Info
                    </Button>
                </ContentContainerToolbar>
            }
        >
            <div className="tab-wrapper">
                <div className="tab-container">
                    {tabs.map((tab, index) => (
                        <div
                            key={tab}
                            onClick={() => setSelectedTab(tab)}
                            className={`tab-item ${selectedTab === tab ? 'active' : ''} ${index === 0 ? 'first' : index === tabs.length - 1 ? 'last' : ''
                                }`}
                        >
                            {tab}
                        </div>
                    ))}
                </div>
                <div>
                    {selectedTab === "Program Logs" ? (
                        <ProgramLogs />
                    ) : selectedTab === "Image Logs" ? (
                        <ImageHistoryLogs onShowRelatedEvents={handleShowRelatedEvents} />
                    ) : selectedTab === "Failed Image Logs" ? (
                        <FailedImageLogs onShowRelatedEvents={handleShowRelatedEvents} />
                    ) : selectedTab === "Related Events" ? (
                        <RelatedEventsLogs filterFormRef={relatedTablefilterFormRef} />
                    ) : null}
                </div>

            </div>

            <SidePopup
                show={showPopup}
                onClose={() => setShowPopup(false)}
                popUpHeader={selectedTab}
                title={popupData.title}
                description={popupData.description}
                sections={popupData.sections}
            />

        </ContentContainer>
    );
};
export default ImageLogs;